import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $getNodeByKey, $getSelection, $isNodeSelection, $isRangeSelection, LexicalNode, NodeKey } from 'lexical';
import ButtonComponent from 'components/ButtonComponent';
import { uploadFile } from 'lib/communication/files';
import './LinkInsertModal.scss';
import { $isImageNode } from '../ImageNode';
import { trackLink } from '../linkRegistry';

const MAX_LINK_FILE_SIZE = 104857600;

type LinkInsertModalProps = {
  show: boolean,
  onClose: () => void
}
type SelectedImage = { nodeKey: NodeKey, link?: string }
const LinkInsertModal = ({ show, onClose }: LinkInsertModalProps) => {
  const [editor] = useLexicalComposerContext();

  const [linkUrlInput, setLinkUrlInput] = useState<string>('');
  const [linkFile, setLinkFile] = useState<File | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const [isEditingLink, setIsEditingLink] = useState<boolean>(false);
  const [isUploadingLinkFile, setIsUploadingLinkFile] = useState<boolean>(false);

  const getSelectedImage = (): SelectedImage | null =>
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isNodeSelection(selection)) return null;
      const nodes: LexicalNode[] = selection.getNodes();
      const node = nodes.length === 1 ? nodes[0] : null;
      if (!$isImageNode(node)) return null;
      return { nodeKey: node.getKey(), link: node.getLink() };
    });

  const setImageLink = (nodeKey: NodeKey, url: string | undefined) => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if($isImageNode(node)) node.setLink(url);
    });
  };

  const hasTextSelection = (): boolean => {
    let selectionOk = false;
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      selectionOk = $isRangeSelection(selection) && !selection.isCollapsed();
    });
    return selectionOk;
  };

  const getSelectedLinkUrl = (): string | null => {
    let url: string | null = null;
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      let node: LexicalNode | null = selection.anchor.getNode();
      while (node !== null) {
        if ($isLinkNode(node)) {
          url = node.getURL();
          return;
        }
        node = node.getParent();
      }
    });
    return url;
  };

  useEffect(() => {
    if (!show) return;

    const selectedImage = getSelectedImage();
    if(selectedImage){
      setIsEditingLink(!!selectedImage.link);
      setLinkUrlInput(selectedImage.link ?? '');
      setLinkError(null);
      return;
    }

    const existingUrl = getSelectedLinkUrl();
    setIsEditingLink(existingUrl !== null);
    if (existingUrl !== null) {
      setLinkUrlInput(existingUrl);
      setLinkError(null);
    } else {
      setLinkUrlInput('');
      setLinkError(hasTextSelection() ? null : 'Zaznacz najpierw fragment tekstu, do którego chcesz dodać link.');
    }
  }, [show]);

  const reset = () => {
    setLinkUrlInput('');
    setLinkFile(null);
    setLinkError(null);
    setIsEditingLink(false);
    setIsUploadingLinkFile(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const applyInsertLink = async () => {
    const selectedImage = getSelectedImage();
    if (!selectedImage && !isEditingLink && !hasTextSelection()) {
      setLinkError('Zaznacz najpierw fragment tekstu, do którego chcesz dodać link.');
      return;
    }
    let target = linkUrlInput.trim();
    if (linkFile) {
      setIsUploadingLinkFile(true);
      try {
        target = await uploadFile(linkFile);
        trackLink(editor,target);
      } catch (e: any) {
        setLinkError(e?.message ?? String(e));
        setIsUploadingLinkFile(false);
        return;
      }
      setIsUploadingLinkFile(false);
    }
    if (!target) {
      setLinkError('Podaj adres URL albo wgraj plik.');
      return;
    }
    if (selectedImage) setImageLink(selectedImage.nodeKey, target)
    else editor.dispatchCommand(TOGGLE_LINK_COMMAND, target);
    handleClose();
  };

  const removeLink = () => {
    const selectedImage = getSelectedImage();
    if (selectedImage) setImageLink(selectedImage.nodeKey, undefined)
    else editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    handleClose();
  };

  const handleLinkFileChange = (file: File | null) => {
    if (file && file.size > MAX_LINK_FILE_SIZE) {
      setLinkFile(null);
      setLinkError('Plik jest za duży. Maksymalny rozmiar to 100MB.');
      return;
    }
    setLinkFile(file);
    setLinkError(null);
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton><Modal.Title>{isEditingLink ? 'Edytuj link' : 'Dodaj link'}</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className='LinkModal'>
          <input
            placeholder='https://...'
            value={linkUrlInput}
            className='LinkModal__input'
            disabled={!!linkFile}
            onChange={(e) => setLinkUrlInput(e.target.value)}
          />
          <div className='LinkModal__separator'>albo</div>
          <input
            type='file'
            onChange={(e) => handleLinkFileChange(e.target.files?.[0] ?? null)}
          />
          {linkError && <div className='LinkModal__error'> {linkError} </div>}
        </div>
        <ButtonComponent
          text={isUploadingLinkFile ? 'Wgrywanie...' : 'Wstaw'}
          disabled={isUploadingLinkFile}
          onClick={applyInsertLink}
        />
        <ButtonComponent
          text='Anuluj'
          onClick={handleClose}
        />
        {isEditingLink && <ButtonComponent text='Usuń link' backgroundColor='#dc3545' onClick={removeLink} />}
      </Modal.Body>
    </Modal>
  );
}

export default LinkInsertModal
